<template>
  <div
    class="w-full mt-6 bg-gray-50 dark:bg-background rounded-t-[20px] flex flex-col px-4 md:px-8 gap-6 lg:gap-8 min-h-screen"
  >
    <div class="flex flex-col lg:flex-row lg:items-start w-full gap-8">
      <!-- Navigation Area -->
      <div class="w-full lg:w-32 flex-shrink-0 lg:pt-2">
        <BackButton />
      </div>

      <!-- Header & Content Section -->
      <div class="flex-1 space-y-10 min-w-0">
        <!-- Header Section -->
        <div
          class="flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div class="space-y-1">
            <h1
              class="text-2xl md:text-[36px] font-bold text-foreground leading-tight"
            >
              My Hubs
            </h1>
            <p class="text-[14px] text-muted-foreground max-w-xl">
              Manage your workspace communities, track active projects, and
              oversee hub members from a single dashboard.
            </p>
          </div>
          <button
            @click="showCreateModal = true"
            class="h-11 px-6 bg-black text-white dark:bg-foreground/10 dark:text-foreground rounded-full font-bold text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-sm active:scale-95"
          >
            <Plus class="w-4 h-4" />
            Create New Hub
          </button>
        </div>

        <!-- Filter Tabs -->
        <div
          class="flex p-1 bg-muted/30 rounded-full border border-border w-fit"
        >
          <button
            v-for="tab in tabs"
            :key="tab"
            @click="activeTab = tab"
            class="px-5 py-2 rounded-full text-sm font-medium transition-all"
            :class="
              activeTab === tab
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            "
          >
            {{ tab }}
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center py-40">
          <Loader />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="hubs.length === 0"
          class="flex flex-col items-center justify-center py-20 bg-card/30 rounded-[40px] border border-dashed border-border gap-6"
        >
          <div
            class="w-24 h-24 bg-muted/40 rounded-full flex items-center justify-center"
          >
            <LayoutDashboard class="w-12 h-12 text-muted-foreground/50" />
          </div>
          <div class="text-center space-y-2">
            <h3 class="text-2xl font-bold">No Hubs Found</h3>
            <p class="text-muted-foreground">
              {{
                activeTab === "Available Hubs"
                  ? "There are no available hubs to join at the moment."
                  : "You haven't created any hubs yet."
              }}
            </p>
          </div>
          <button
            v-if="activeTab === 'My Hubs'"
            @click="showCreateModal = true"
            class="text-primary font-bold text-lg hover:underline"
          >
            Get started by creating your first hub
          </button>
        </div>

        <!-- Hubs List (Compact Refactor) -->
        <div v-else class="space-y-4">
          <div
            v-for="hub in hubs"
            :key="hub.id"
            @click="goToHub(hub.id)"
            class="group relative bg-card border border-border rounded-2xl p-4 hover:bg-muted/5 transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <!-- Left: Hub Initial Box (Neutral) -->
            <div class="shrink-0">
              <div
                class="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-foreground font-bold text-xl border border-border/50 shadow-sm"
              >
                {{ hub.name ? hub.name.trim().charAt(0).toUpperCase() : "H" }}
              </div>
            </div>

            <!-- Middle: Name & Data -->
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-0.5">
                <h3 class="text-[17px] font-bold text-foreground truncate">
                  {{ hub.name }}
                </h3>
              </div>
              <p class="text-[14px] text-muted-foreground line-clamp-1 mb-2">
                {{ hub.description || "No description available." }}
              </p>

              <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div
                  v-if="hub.address"
                  class="flex items-center gap-1.5 text-[13px] text-muted-foreground"
                >
                  <MapPin class="w-3.5 h-3.5 text-muted-foreground" />
                  <span class="truncate max-w-[200px]">{{ hub.address }}</span>
                </div>
                <div
                  class="flex items-center gap-1.5 text-[13px] text-muted-foreground"
                >
                  <Users class="w-3.5 h-3.5 text-muted-foreground" />
                  {{ hub.capacity || 0 }} capacity
                </div>
              </div>
            </div>

            <div class="flex items-center shrink-0">
              <div
                class="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center group-hover:bg-primary/10 group-hover:text-primary transition-colors"
              >
                <ChevronRight
                  class="w-5 h-5 group-hover:translate-x-0.5 transition-transform"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <WorkspaceCreateModal
      :isOpen="showCreateModal"
      @close="showCreateModal = false"
      @created="_onHubCreated"
    />
  </div>
</template>

<script>
import apiClient from "@/axios";
import WorkspaceCreateModal from "@/components/modals/WorkspaceCreateModal.vue";
import BackButton from "@/components/BackButton.vue";
import Loader from "@/components/ui/Loader.vue";
import {
  Plus,
  LayoutDashboard,
  ChevronRight,
  Users,
  MapPin,
} from "lucide-vue-next";

export default {
  name: "HubsListingView",
  components: {
    WorkspaceCreateModal,
    BackButton,
    Plus,
    LayoutDashboard,
    ChevronRight,
    Users,
    MapPin,
    Loader,
  },
  data() {
    return {
      hubs: [],
      loading: true,
      showCreateModal: false,
      activeTab: "Available Hubs",
      tabs: ["Available Hubs", "My Hubs"],
    };
  },
  watch: {
    activeTab() {
      this.fetchHubs();
    },
  },
  mounted() {
    this.fetchHubs();
  },
  methods: {
    async fetchHubs() {
      try {
        this.loading = true;
        const endpoint =
          this.activeTab === "Available Hubs"
            ? "/web/workspace/available/list"
            : "/web/workspace/my/list";
        const response = await apiClient.get(endpoint);
        this.hubs = response.data?.data || response.data || [];
      } catch (error) {
        console.error("Error fetching hubs:", error);
        this.$toast?.error("Error", "Failed to load hubs. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    goToHub(id) {
      this.$router.push(`/my-hubs/${id}`);
    },
    _onHubCreated() {
      this.showCreateModal = false;
      this.$toast?.success("Success", "Hub created successfully!");
      this.fetchHubs();
    },
  },
};
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
